import { useEffect, useState, useCallback } from 'react';
import {
    classificationApi,
    type SupplierListResponse, type CustomerListResponse,
    type YearRow, type ClassificationConfig, type Tier,
} from '../services/classificationApi';

export const COMPANIES = ['dch', 'dit', 'dsp', 'dtr'] as const;
export type Company = typeof COMPANIES[number];

const STORAGE_YEAR = 'spc-classification-year';
const STORAGE_COMPANY = 'spc-classification-company';
const STORAGE_TAB = 'spc-classification-tab';

export type ClassificationTab = 'suppliers' | 'customers';

export function useClassificationState() {
    const [year, setYearState] = useState<number>(() => {
        const v = sessionStorage.getItem(STORAGE_YEAR);
        return v ? Number(v) : new Date().getFullYear();
    });
    const [company, setCompanyState] = useState<Company>(() => {
        const v = sessionStorage.getItem(STORAGE_COMPANY) as Company | null;
        return v && (COMPANIES as readonly string[]).includes(v) ? v : 'dch';
    });
    const [tab, setTabState] = useState<ClassificationTab>(() => {
        const v = sessionStorage.getItem(STORAGE_TAB) as ClassificationTab | null;
        return v === 'customers' ? 'customers' : 'suppliers';
    });

    const setYear = (y: number) => { setYearState(y); sessionStorage.setItem(STORAGE_YEAR, String(y)); };
    const setCompany = (c: Company) => { setCompanyState(c); sessionStorage.setItem(STORAGE_COMPANY, c); };
    const setTab = (t: ClassificationTab) => { setTabState(t); sessionStorage.setItem(STORAGE_TAB, t); };

    return { year, setYear, company, setCompany, tab, setTab };
}

export function useYears() {
    const [years, setYears] = useState<YearRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await classificationApi.listYears();
            setYears(r.years);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);
    return { years, loading, error, refresh };
}

export function useConfig(year: number) {
    const [config, setConfig] = useState<ClassificationConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const c = await classificationApi.getConfig(year);
            setConfig(c);
        } catch (e) {
            setError((e as Error).message);
            setConfig(null);
        } finally {
            setLoading(false);
        }
    }, [year]);

    useEffect(() => { refresh(); }, [refresh]);
    return { config, loading, error, refresh };
}

export function useSuppliers(year: number, company: Company) {
    const [data, setData] = useState<SupplierListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        let cancel = false;
        setLoading(true); setError(null);
        classificationApi.listSuppliers(year, company)
            .then(r => { if (!cancel) setData(r); })
            .catch(e => { if (!cancel) setError((e as Error).message); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [year, company, refreshTrigger]);

    return {
        data, loading, error,
        refresh: () => setRefreshTrigger(t => t + 1),
    };
}

export function useCustomers(year: number, company: Company, opts: {
    salesManager?: string; tier?: Tier; onlyChanged?: boolean; onlyOverrides?: boolean; search?: string;
} = {}) {
    const [data, setData] = useState<CustomerListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const optsKey = JSON.stringify(opts);

    useEffect(() => {
        let cancel = false;
        setLoading(true); setError(null);
        classificationApi.listCustomers(year, company, opts)
            .then(r => { if (!cancel) setData(r); })
            .catch(e => { if (!cancel) setError((e as Error).message); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, company, optsKey, refreshTrigger]);

    return {
        data, loading, error,
        refresh: () => setRefreshTrigger(t => t + 1),
    };
}
